import { BusinessBusiness } from "../types";
import { normalizeTags } from "./tagUtils";
import { analyzeBusinessProspect } from "./opportunityFinder";
import { getPreferredCategories } from "./opportunityHelpers";
import { generateJobEnrichment } from "./jobEnrichment";
import { AudienceModeId } from "../config/audienceModes";
import { calculateAudienceScore, AudienceScoreResult } from "./audienceScoring";
import { parseBusinessQuery, calculateQueryRelevanceScore } from "./businessSearchEngine";

export interface ScoreBreakdownDetail {
  component: "base" | "relevance" | "proximity" | "personalization" | "employment_quality";
  label: string;
  points: number;
  description: string;
  maxPoints: number;
}

export interface UpgradedOrganicScoreResult {
  totalScore: number;
  baseScore: number;
  relevanceBoost: number;
  proximityBoost: number;
  personalizationBoost: number;
  audienceScoreResult?: AudienceScoreResult;
  breakdown: ScoreBreakdownDetail[];
  relevanceExplanation: string;
  proximityExplanation: string;
  personalizationExplanation: string;
  prospect: any;
  negativeSignalData: {
    count: number;
    severityScore: number;
    criticalCount: number;
    signals: Array<{
      key: string;
      label: string;
      severity: "low" | "medium" | "high" | "critical";
      category: string;
    }>;
  };
  uncappedScore: number;

  // Advanced Google-Style discovery metrics:
  totalSmartScore: number;
  overallScore: number;
  leadScore: number;
  discoveryScore: number;
  trustScore: number;
  websiteQualityScore: number;
  contactabilityScore: number;
  activityScore: number;
  opportunityFitScore: number;
  hiringSignalScore: number;
  localDemandScore: number;
  commuteScore: number;
  distanceScore: number;
  userBehaviorScore: number;
  relevanceScore: number;
  finalRankScore: number;
  badges: string[];
  strengths: string[];
  opportunitiesToImprove: string[];
  reasonCodes: string[];
  suggestedNextAction: string;
  scoreExplanation: string;
  scoreVersion: string;
}

export interface RankingOptions {
  prioritizeHiring?: boolean;
  prioritizeTransparency?: boolean;
  prioritizeFlexibility?: boolean;
  prioritizeCommute?: boolean;
  prioritizeGrowthSecurity?: boolean;
  trackingEnabled?: boolean;
}

export function assessNegativeSignals(
  business: BusinessBusiness,
  prospect: any
): {
  count: number;
  severityScore: number;
  criticalCount: number;
  signals: Array<{
    key: string;
    label: string;
    severity: "low" | "medium" | "high" | "critical";
    category: string;
  }>;
} {
  const signals: Array<{
    key: string;
    label: string;
    severity: "low" | "medium" | "high" | "critical";
    category: string;
  }> = [];

  const warnings = prospect?.categorizedSignals?.warnings || [];

  // 1. Permanently or temporarily closed
  const isClosed = warnings.includes("Possibly Closed") || 
                   (business.rating !== undefined && business.rating > 0 && business.rating < 2.5) ||
                   (business.hiringStatus || "").toLowerCase() === "closed" ||
                   business.pipeline_stage === "Blocked" ||
                   business.pipelineStage === "Blocked";
  if (isClosed) {
    signals.push({
      key: "possibly_closed",
      label: "Business is permanently or temporarily closed",
      severity: "critical",
      category: "status"
    });
  }

  // 2. Unsafe website/domain / SSL Security Risk
  if (business.website && business.websiteAudit && business.websiteAudit.https === false) {
    signals.push({
      key: "ssl_security_risk",
      label: "Website lacks secure SSL/HTTPS encryption",
      severity: "high",
      category: "website"
    });
  }

  // 3. Broken website / Website Review Failed
  const isWebFailed = business.website && (
    business.websiteAudit?.failed || 
    business.websiteReviewStatus === "failed" || 
    business.websiteAudit?.exists === false
  );
  if (isWebFailed) {
    signals.push({
      key: "broken_website",
      label: "Official website is broken or failed audit checks",
      severity: "high",
      category: "website"
    });
  }

  // 4. Severe trust / reputation failure
  if (business.rating !== undefined && business.rating > 0 && business.rating < 2.5) {
    signals.push({
      key: "severe_reputation_risk",
      label: "Google rating is critically low (below 2.5★)",
      severity: "high",
      category: "trust"
    });
  }

  // 5. Incomplete business profile (both missing)
  if (!business.website && !business.phone) {
    signals.push({
      key: "severely_incomplete_profile",
      label: "Severely incomplete business details (missing both website and phone)",
      severity: "high",
      category: "trust"
    });
  }

  // 6. Poor review signals (Rating < 4.0 but >= 2.5)
  if (business.rating !== undefined && business.rating > 0 && business.rating >= 2.5 && business.rating < 4.0) {
    signals.push({
      key: "poor_review_signals",
      label: "Google rating is below the 4.0 benchmark (reputation risk)",
      severity: "medium",
      category: "trust"
    });
  }

  // 7. Outdated information
  if (business.website && business.websiteAudit?.outdatedDetails === true) {
    signals.push({
      key: "outdated_information",
      label: "Website contains outdated business information",
      severity: "medium",
      category: "website"
    });
  }

  // 8. Poor mobile experience / Mobile responsiveness failure
  if (business.website && business.websiteAudit?.mobileFriendly === false) {
    signals.push({
      key: "mobile_friendliness_gap",
      label: "Website lacks dynamic mobile-responsive optimization",
      severity: "medium",
      category: "website"
    });
  }

  // 9. No usable contact method / Contact Search Failed
  if (business.crawlStatus === "error") {
    signals.push({
      key: "contact_search_failed",
      label: "Contact verification crawl failed",
      severity: "medium",
      category: "contactability"
    });
  }

  // 10. Duplicate Listing
  if (warnings.includes("Possible Duplicate")) {
    signals.push({
      key: "possible_duplicate",
      label: "Listing flagged as a potential duplicate",
      severity: "medium",
      category: "trust"
    });
  }

  // 11. Missing website (only if phone exists to distinguish from severely incomplete)
  if (!business.website && business.phone) {
    signals.push({
      key: "missing_website",
      label: "Missing business website",
      severity: "medium",
      category: "website"
    });
  }

  // 12. Missing phone number (only if website exists)
  if (!business.phone && business.website) {
    signals.push({
      key: "missing_phone",
      label: "Missing direct phone number",
      severity: "low",
      category: "contactability"
    });
  }

  // 13. Missing address listing
  if (!business.address) {
    signals.push({
      key: "missing_address",
      label: "Missing business address listing",
      severity: "low",
      category: "trust"
    });
  }

  // 14. Unrated business
  if (business.rating === undefined || business.rating === 0) {
    signals.push({
      key: "unrated_business",
      label: "Unrated business (unknown customer satisfaction)",
      severity: "low",
      category: "trust"
    });
  }

  // 15. Low reviews count
  if (business.rating !== undefined && (business.reviewsCount === undefined || business.reviewsCount < 10)) {
    signals.push({
      key: "low_reviews_count",
      label: "Low review count footprint (unverified local popularity)",
      severity: "low",
      category: "trust"
    });
  }

  // 16. No direct contact emails found
  if (!business.contacts || business.contacts.length === 0) {
    signals.push({
      key: "missing_emails",
      label: "No direct contact emails found",
      severity: "low",
      category: "contactability"
    });
  }

  // 17. Low confidence contact info
  if (business.contacts && business.contacts.some(c => c.confidence === "Low")) {
    signals.push({
      key: "low_confidence_contact",
      label: "Contact details need verification (low confidence)",
      severity: "low",
      category: "contactability"
    });
  }

  // 18. Missing hiring information (no active hiring status or signals)
  const isHiring = (prospect?.categorizedSignals?.workOpportunities || []).some(
    (tag: string) => ["Currently Hiring", "Hiring Multiple Roles", "High Hiring Signal"].includes(tag)
  ) || (business.hiringStatus || "").toLowerCase() === "active";
  if (!isHiring) {
    signals.push({
      key: "missing_hiring_info",
      label: "No active local hiring information confirmed",
      severity: "low",
      category: "employment"
    });
  }

  // 19. Missing digital contact form
  if (business.website && business.websiteAudit && business.websiteAudit.contactFormFound === false) {
    signals.push({
      key: "missing_contact_form",
      label: "Missing online contact or quote form on website",
      severity: "low",
      category: "website"
    });
  }

  // 20. Missing online booking system
  if (business.website && business.websiteAudit && business.websiteAudit.bookingButtonFound === false) {
    signals.push({
      key: "missing_booking_system",
      label: "Missing online booking scheduling system on website",
      severity: "low",
      category: "website"
    });
  }

  // 21. Missing SEO optimization
  if (business.website && business.websiteAudit && (business.websiteAudit.seoTitleExists === false || business.websiteAudit.metaDescriptionExists === false)) {
    signals.push({
      key: "missing_seo",
      label: "Missing HTML SEO title or description",
      severity: "low",
      category: "website"
    });
  }

  let severityScore = 0;
  let criticalCount = 0;

  for (const sig of signals) {
    if (sig.severity === "critical") {
      severityScore += 15;
      criticalCount++;
    } else if (sig.severity === "high") {
      severityScore += 7;
    } else if (sig.severity === "medium") {
      severityScore += 3;
    } else if (sig.severity === "low") {
      severityScore += 1;
    }
  }

  return {
    count: signals.length,
    severityScore,
    criticalCount,
    signals
  };
}

/**
 * Calculates a unified, multi-signal, organic matchmaking score (10 - 100)
 * strictly without any sponsored ranking/boosting bias, containing advanced
 * search metrics, badges, and explanation logs.
 */
export function getUpgradedOrganicScore(
  business: BusinessBusiness,
  distanceInMiles?: number | null,
  searchKeyword?: string,
  externalPrefs?: Record<string, number>,
  rankingOptions?: RankingOptions,
  audienceMode?: AudienceModeId
): UpgradedOrganicScoreResult {
  const prospect = analyzeBusinessProspect(business, distanceInMiles);
  const activeAudienceMode = audienceMode || "job_seeker";
  const audienceScoreResult = calculateAudienceScore(business, distanceInMiles ?? null, searchKeyword || "", activeAudienceMode, prospect);
  
  // Base score foundation from CRM overlay score or prospect quality metrics
  const baseScore = (business as any).Business_score !== undefined && (business as any).Business_score !== null
    ? Number((business as any).Business_score) 
    : (business.Businessescore !== undefined && business.Businessescore !== null
        ? Number(business.Businessescore)
        : prospect.score);
  
  // 1. Calculate Search Text Relevance Boost (up to +25)
  let relevanceBoost = 0;
  let relevanceExplanation = "No search keyword specified.";
  let isIrrelevantCandidate = false;
  
  if (searchKeyword && searchKeyword.trim()) {
    const parsedQuery = parseBusinessQuery(searchKeyword);
    const includeAdult = (rankingOptions as any)?.includeAdultOriented ?? (rankingOptions as any)?.includeAdult ?? false;
    const relResult = calculateQueryRelevanceScore(business, parsedQuery, distanceInMiles, includeAdult);
    
    if (relResult.validationResult && !relResult.validationResult.isRelevant) {
      isIrrelevantCandidate = true;
      relevanceBoost = 0;
      relevanceExplanation = relResult.matchExplanation || "Candidate failed relevance threshold for query.";
    } else {
      // Convert 0-100 relevance score to +0 to +25 boost
      relevanceBoost = Math.round((relResult.score / 100) * 25);
      relevanceExplanation = `${relResult.matchExplanation} (+${relevanceBoost} pts)`;
    }
  }

  // 2. Calculate Commute / Proximity Boost (up to +15)
  let proximityBoost = 0;
  let proximityExplanation = "Proximity could not be computed (missing search location center).";
  
  if (distanceInMiles !== null && distanceInMiles !== undefined) {
    if (distanceInMiles <= 1.0) {
      proximityBoost = 15;
      proximityExplanation = `Hyper-local walking range (${distanceInMiles.toFixed(1)} miles) (+15 pts)`;
    } else if (distanceInMiles <= 3.0) {
      proximityBoost = 10;
      proximityExplanation = `Very close commute (${distanceInMiles.toFixed(1)} miles) (+10 pts)`;
    } else if (distanceInMiles <= 8.0) {
      proximityBoost = 5;
      proximityExplanation = `Reasonable driving proximity (${distanceInMiles.toFixed(1)} miles) (+5 pts)`;
    } else if (distanceInMiles <= 15.0) {
      proximityBoost = 2;
      proximityExplanation = `Extended commute range (${distanceInMiles.toFixed(1)} miles) (+2 pts)`;
    } else if (distanceInMiles > 20.0) {
      proximityBoost = -10;
      proximityExplanation = `Distant proximity (${distanceInMiles.toFixed(1)} miles) (-10 pts penalty)`;
    } else {
      proximityExplanation = `Moderate proximity (${distanceInMiles.toFixed(1)} miles) (+0 pts)`;
    }
  }

  // 3. Calculate Personalized Interaction Affinity Boost (capped at -12 to +12)
  let personalizationBoost = 0;
  let personalizationExplanation = "No past user interactions logged for this business category.";
  
  // Check if tracking is explicitly disabled in rankingOptions or externalPrefs
  const isTrackingDisabled = (rankingOptions as any)?.trackingEnabled === false;

  if (isTrackingDisabled) {
    personalizationExplanation = "Machine-Matching Tracker is disabled in Settings.";
  } else if (externalPrefs) {
    // Check if externalPrefs is a full UserPreferenceProfile
    if ("confidence" in externalPrefs && "categoryScores" in externalPrefs) {
      const profile = externalPrefs as any;
      const confidence = profile.confidence || 0;
      if (confidence < 0.1 || profile.totalInteractionsCount < 5) {
        personalizationExplanation = `Gathering initial learning interactions (${profile.totalInteractionsCount}/5 needed for personalization).`;
      } else {
        const catScore = business.category ? (profile.categoryScores[business.category] || 0) : 0;
        let rawBoost = 0;
        if (catScore > 0) {
          rawBoost = Math.min(12, Math.round(catScore * 1.2));
        } else if (catScore < 0) {
          rawBoost = Math.max(-12, Math.round(catScore * 1.2));
        }

        personalizationBoost = Math.round(rawBoost * confidence);
        // Clamp to [-12, +12]
        personalizationBoost = Math.max(-12, Math.min(12, personalizationBoost));

        if (personalizationBoost > 0) {
          personalizationExplanation = `Matches your learned category preferences for "${business.category}" (+${personalizationBoost} pts, ${Math.round(confidence * 100)}% confidence)`;
        } else if (personalizationBoost < 0) {
          personalizationExplanation = `Penalized based on past negative interactions in "${business.category}" (${personalizationBoost} pts)`;
        } else {
          personalizationExplanation = `No specific preference signal recorded for "${business.category}".`;
        }
      }
    } else {
      // Legacy Record<string, number> fallback
      if (business.category) {
        const scoreFactor = (externalPrefs as Record<string, number>)[business.category] || 0;
        if (scoreFactor > 0) {
          personalizationBoost = Math.min(12, Math.max(0, Math.round(scoreFactor * 1.2)));
          personalizationExplanation = `Active category affinity for "${business.category}" (${scoreFactor} past interactions) (+${personalizationBoost} pts)`;
        }
      }
    }
  } else {
    // Fallback if no profile passed
    if (business.category) {
      const prefs = getPreferredCategories();
      const scoreFactor = prefs[business.category] || 0;
      if (scoreFactor > 0) {
        personalizationBoost = Math.min(12, Math.max(0, Math.round(scoreFactor * 1.2)));
        personalizationExplanation = `Active category affinity for "${business.category}" (+${personalizationBoost} pts)`;
      }
    }
  }


  // 4. Calculate Employment Quality Boost (up to +25)
  const enrichment = business.jobEnrichment || generateJobEnrichment(business);
  // Ensure we attach it so components downstream can access it
  business.jobEnrichment = enrichment;

  // Foundation: original scoring variables
  let baselineBoost = 0;
  if (enrichment.compensation.transparencyScore >= 80) baselineBoost += 5;
  if (enrichment.workplaceFlexibility.locationType !== "on-site" || enrichment.workplaceFlexibility.flexibleHours) baselineBoost += 5;
  if (enrichment.jobSecurity.securityIndicator === "high") {
    baselineBoost += 5;
  } else if (enrichment.jobSecurity.securityIndicator === "medium") {
    baselineBoost += 3;
  }
  if (enrichment.careerGrowth.trainingAvailable) baselineBoost += 3;
  if (enrichment.careerGrowth.tuitionSupport) baselineBoost += 2;
  if (enrichment.companyCulture.rating >= 4.0) {
    baselineBoost += 5;
  } else if (enrichment.companyCulture.rating >= 3.5) {
    baselineBoost += 3;
  }

  // Additive advanced signals, scaled by confidence multiplier
  let advancedSignalsBoost = 0;

  // A. Actively Hiring Signal (High-priority parameter)
  const localHiringScore = prospect.scores?.hiringScore !== undefined ? prospect.scores.hiringScore : 5;
  if (enrichment.metadata.hiringLikelihood === "high" || localHiringScore >= 75 || business.hiringStatus === "active") {
    advancedSignalsBoost += 4;
  }

  // B. Compensation Quality Indicators
  if (enrichment.compensation.basePayMin && enrichment.compensation.basePayMin >= 22) {
    advancedSignalsBoost += 2;
  }
  if (enrichment.compensation.bonusAvailable || enrichment.compensation.commissionAvailable) {
    advancedSignalsBoost += 1;
  }
  if (enrichment.compensation.healthcareBenefitsObj?.medical && enrichment.compensation.healthcareBenefitsObj?.dental) {
    advancedSignalsBoost += 2;
  }
  if (enrichment.compensation.retirementBenefitsObj?.available && enrichment.compensation.retirementBenefitsObj?.employerMatch) {
    advancedSignalsBoost += 2;
  }

  // C. Workplace Flexibility Quality Indicators
  if (enrichment.workplaceFlexibility.workLocationType === "hybrid" || enrichment.workplaceFlexibility.workLocationType === "remote") {
    advancedSignalsBoost += 2;
  }
  if (enrichment.workplaceFlexibility.partTimeAvailable && enrichment.workplaceFlexibility.fullTimeAvailable) {
    advancedSignalsBoost += 1;
  }

  // D. Job Security Quality Indicators
  if (enrichment.jobSecurity.stabilityScore && enrichment.jobSecurity.stabilityScore >= 75) {
    advancedSignalsBoost += 2;
  }
  if (enrichment.jobSecurity.tenureYrs && enrichment.jobSecurity.tenureYrs >= 3.0) {
    advancedSignalsBoost += 1;
  }

  // E. Career Growth Quality Indicators
  if (enrichment.careerGrowth.advancementMentioned || enrichment.careerGrowth.mentorshipAvailable) {
    advancedSignalsBoost += 2;
  }
  if (enrichment.careerGrowth.certificationsSupported || enrichment.careerGrowth.internalMobilityMentioned) {
    advancedSignalsBoost += 1;
  }

  // F. Company Culture Quality Indicators
  if (enrichment.companyCulture.cultureScore && enrichment.companyCulture.cultureScore >= 75) {
    advancedSignalsBoost += 2;
  }
  if (enrichment.companyCulture.positiveIndicators && enrichment.companyCulture.positiveIndicators.length >= 2) {
    advancedSignalsBoost += 1;
  }

  // Apply confidence scaling to the advanced signals so that low-confidence data has less weight,
  // but protect from negative scoring (no penalties are ever applied for missing/unverified data)
  const confidenceMultiplier = (enrichment.metadata.confidenceScore || 65) / 100;
  const scaledAdvancedBoost = Math.max(0, advancedSignalsBoost * confidenceMultiplier);

  // Total quality boost is the sum of baseline and scaled advanced signals, clamped to a maximum of 25 points
  const jobQualityBoost = Math.min(25, Math.round(baselineBoost + scaledAdvancedBoost));

  // Additive optional ranking variables based on user priority toggles
  let optionalsBoost = 0;
  if (rankingOptions) {
    if (rankingOptions.prioritizeHiring) {
      const localHiringScore = prospect.scores?.hiringScore !== undefined ? prospect.scores.hiringScore : 5;
      if (enrichment.metadata.hiringLikelihood === "high" || localHiringScore >= 75 || business.hiringStatus === "active") {
        optionalsBoost += 8;
      }
    }
    if (rankingOptions.prioritizeTransparency) {
      if (enrichment.compensation.transparencyScore >= 80) {
        optionalsBoost += 8;
      }
    }
    if (rankingOptions.prioritizeFlexibility) {
      if (enrichment.workplaceFlexibility.locationType !== "on-site" || enrichment.workplaceFlexibility.flexibleHours) {
        optionalsBoost += 8;
      }
    }
    if (rankingOptions.prioritizeCommute) {
      if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 3.0) {
        optionalsBoost += 8;
      }
    }
    if (rankingOptions.prioritizeGrowthSecurity) {
      if (enrichment.jobSecurity.securityIndicator === "high" || enrichment.careerGrowth.advancementMentioned) {
        optionalsBoost += 8;
      }
    }
  }

  // Combine and clamp overall score to 0 - 100
  const negativeSignalData = assessNegativeSignals(business, prospect);

  let unclampedTotal = baseScore + relevanceBoost + proximityBoost + personalizationBoost + jobQualityBoost + optionalsBoost;
  unclampedTotal -= negativeSignalData.severityScore;

  const hasCriticalIssue = negativeSignalData.criticalCount > 0 || 
                           negativeSignalData.signals.some(s => s.severity === "high" && s.key === "ssl_security_risk") ||
                           (business.rating !== undefined && business.rating > 0 && business.rating < 2.5);

  let totalScore = Math.max(0, Math.min(100, Math.round(unclampedTotal)));
  if (hasCriticalIssue) {
    totalScore = Math.min(30, totalScore);
    unclampedTotal = Math.min(30, unclampedTotal);
  }

  // Generate granular breakdowns
  const breakdown: ScoreBreakdownDetail[] = [
    {
      component: "base",
      label: "Organic Foundation",
      points: baseScore,
      maxPoints: 100,
      description: "Based on rating, reviews, website audits, contact visibility, local demand, and hiring signals."
    }
  ];

  if (relevanceBoost > 0) {
    breakdown.push({
      component: "relevance",
      label: "Search Relevance",
      points: relevanceBoost,
      maxPoints: 25,
      description: relevanceExplanation
    });
  }

  if (proximityBoost !== 0) {
    breakdown.push({
      component: "proximity",
      label: "Commute Proximity",
      points: proximityBoost,
      maxPoints: 15,
      description: proximityExplanation
    });
  }

  if (personalizationBoost > 0) {
    breakdown.push({
      component: "personalization",
      label: "Category Affinity",
      points: personalizationBoost,
      maxPoints: 15,
      description: personalizationExplanation
    });
  }

  if (jobQualityBoost > 0) {
    breakdown.push({
      component: "employment_quality",
      label: "Employment Quality",
      points: jobQualityBoost,
      maxPoints: 25,
      description: `Compensation transparency, workplace flexibility, job security, and training/growth support (+${jobQualityBoost} pts).`
    });
  }

  if (optionalsBoost > 0) {
    breakdown.push({
      component: "personalization",
      label: "Match Priority Alignment",
      points: optionalsBoost,
      maxPoints: 40,
      description: `User-selected match priority settings matched this opportunity (+${optionalsBoost} pts).`
    });
  }

  if (negativeSignalData.severityScore > 0) {
    breakdown.push({
      component: "base",
      label: "Digital Weakness Penalty",
      points: -negativeSignalData.severityScore,
      maxPoints: 0,
      description: `Penalties applied for ${negativeSignalData.count} identified digital weakness(es) or data gap(s) (-${negativeSignalData.severityScore} pts).`
    });
  }

  // Map sub-scores & detailed factors requested
  const totalSmartScore = isIrrelevantCandidate ? 0 : totalScore;
  const overallScore = isIrrelevantCandidate ? 0 : totalScore;
  const leadScore = prospect.score || 50;
  
  const trustScore = prospect.scores?.trustScore !== undefined ? prospect.scores.trustScore : 50;
  
  // Website Quality calculation
  let websiteQualityScore = 50;
  if (business.website) {
    websiteQualityScore = 75;
    if (business.websiteAudit) {
      const audit = business.websiteAudit;
      let auditPoints = 0;
      if (audit.https) auditPoints += 10;
      if (audit.mobileFriendly) auditPoints += 10;
      if (audit.contactFormFound) auditPoints += 5;
      websiteQualityScore = 75 + auditPoints;
    }
  } else {
    websiteQualityScore = 20;
  }

  const contactabilityScore = business.website && business.phone ? 95 : (business.website || business.phone ? 70 : 30);
  
  let activityScore = 50;
  if (business.rating !== undefined) {
    activityScore = Math.min(100, Math.round(business.rating * 15 + (business.reviewsCount ? Math.min(25, business.reviewsCount / 3) : 0)));
  }

  const opportunityFitScore = prospect.scores?.opportunityScore !== undefined ? prospect.scores.opportunityScore : 50;
  const hiringSignalScore = prospect.scores?.hiringScore !== undefined ? prospect.scores.hiringScore : 5;
  
  const localDemandScore = business.reviewsCount ? Math.min(100, Math.round(business.reviewsCount * 1.5)) : 30;
  const commuteScore = distanceInMiles !== null && distanceInMiles !== undefined ? Math.max(10, Math.min(100, Math.round(100 - distanceInMiles * 5.5))) : 60;
  const distanceScore = distanceInMiles !== null && distanceInMiles !== undefined ? Math.max(10, Math.min(100, Math.round(100 - distanceInMiles * 4))) : 60;
  
  const userBehaviorScore = Math.round(Math.min(100, personalizationBoost * 6.6) || 50);
  const relevanceScore = isIrrelevantCandidate ? 0 : Math.round(Math.min(100, relevanceBoost * 4) || 50);
  const finalRankScore = isIrrelevantCandidate ? 0 : totalScore;

  const discoveryScore = isIrrelevantCandidate ? 0 : Math.max(10, Math.min(100, Math.round(
    (relevanceScore * 0.35) + (contactabilityScore * 0.25) + (activityScore * 0.2) + (trustScore * 0.2)
  )));

  // Generate logical badges list
  const badges: string[] = [];
  if (business.website && business.phone) {
    badges.push("Highly Reachable");
  }
  if (business.rating && business.rating >= 4.6 && business.reviewsCount && business.reviewsCount >= 40) {
    badges.push("Elite Local Reputation");
  }
  if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 1.5) {
    badges.push("Immediate Walk-In");
  }
  if (prospect.score >= 80) {
    badges.push("Top Discovery Pick");
  }
  if (hiringSignalScore >= 75) {
    badges.push("Hiring Potential");
  }

  // Pull strengths/opportunities
  const strengths = prospect.strengths || [];
  const opportunitiesToImprove = prospect.opportunitiesToImprove || [];
  const suggestedNextAction = prospect.recommendedNextStep || "Reach out to verify owner and introduce solutions.";

  // Generate logic reason codes
  const reasonCodes: string[] = [];
  if (relevanceBoost > 0) reasonCodes.push("keyword_match");
  if (business.website) reasonCodes.push("website_available");
  if (business.phone) reasonCodes.push("phone_available");
  if (business.rating && business.rating >= 4.0) reasonCodes.push("high_rating");
  if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 5.0) reasonCodes.push("close_proximity");
  if (personalizationBoost > 0) reasonCodes.push("category_affinity");

  if (isIrrelevantCandidate) {
    if (audienceScoreResult) {
      audienceScoreResult.score = 0;
      audienceScoreResult.level = "Low";
      audienceScoreResult.levelRank = 1;
    }
    totalScore = 0;
  }

  const scoreExplanation = `This matchmaking score of ${totalScore} was calculated using organic relevance factors. The base business opportunity evaluation is ${baseScore}/100. It is adjusted with a search keyword relevance boost of +${relevanceBoost}, a geographic commute proximity adjustment of +${proximityBoost}, a user interaction affinity boost of +${personalizationBoost}, and an employment quality boost of +${jobQualityBoost} based on compensation transparency, flexibility, security, and culture. This calculation is purely organic and never influenced by sponsored bids.`;

  return {
    totalScore: isIrrelevantCandidate ? 0 : (audienceScoreResult ? audienceScoreResult.score : totalScore),
    baseScore,
    relevanceBoost,
    proximityBoost,
    personalizationBoost,
    audienceScoreResult,
    breakdown,
    relevanceExplanation,
    proximityExplanation,
    personalizationExplanation,
    prospect,
    negativeSignalData,
    uncappedScore: unclampedTotal,

    totalSmartScore,
    overallScore,
    leadScore,
    discoveryScore,
    trustScore,
    websiteQualityScore,
    contactabilityScore,
    activityScore,
    opportunityFitScore,
    hiringSignalScore,
    localDemandScore,
    commuteScore,
    distanceScore,
    userBehaviorScore,
    relevanceScore,
    finalRankScore,
    badges,
    strengths,
    opportunitiesToImprove,
    reasonCodes,
    suggestedNextAction,
    scoreExplanation,
    scoreVersion: "2.0-google-style"
  };
}
